import React, { useEffect, useState } from "react";
import {
  ApiDelete,
  ApiGet,
  ApiPost,
  ApiPut,
} from "../helpers/API/ApiData";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import List from "@mui/material/List";
import Toolbar from "@mui/material/Toolbar";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Box, Paper, Typography, TextField, Button, CircularProgress, Tooltip } from "@mui/material";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";
import moment from "moment";

import { DataTable, TableColumnDef } from "../components/DataTable";
import { SelfHostedEditor } from "../components/TextEditor";
import { PageHeader } from "../components/PageHeader";

import { createSanitizedMarkup } from "../utils/createSanitizedMarkup";

import { toast, ToastContainer } from "react-toastify";
import { usePermissions } from "../hooks/usePermissions";
import { useContentLanguage } from "../hooks";
import { LanguageTabs } from "../components/LanguageTabs";

interface EditorState {
  content: string;
  content_ger: string;
}

const Privacy = () => {
  const { canCreate, canEdit, canDelete } = usePermissions("/privacy");
  const [lang, setLang] = useContentLanguage();
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [countPerPage, setCountPerPage] = useState(10);
  const [isPrivacyData, setIsPrivacyData] = useState<any>();
  const [inputValue, setInputValue] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [isEditApi, setIsEditApi] = useState(false);
  const [idForPrivacyEdit, setIdForPrivacyEdit] = useState("");
  const [search, setSearch] = useState("");
  const [isPrivacyModal, setIsPrivacyModal] = useState(false);
  const [show, setShow] = useState(false);
  const [privacyIdForDelete, setPrivacyIdForDelete] = useState("");

  const [editorState, setEditorState] = useState<EditorState>({
    content: "",
    content_ger: "",
  });

  const handleOnChange = (e: any) => {
    const { name, value } = e.target;
    setInputValue({ ...inputValue, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handlePrivacyModalClose = () => {
    setIsPrivacyModal(false);
    setIsEditApi(false);
    setErrors({});
    setInputValue({});
    setEditorState({ content: "", content_ger: "" });
  };

  const handleClose = () => {
    setShow(false);
  };

  useEffect(() => {
    getPrivacyDetails();
  }, [page, countPerPage, search]);

  const getPrivacyDetails = async () => {
    setLoadingData(true);
    const searchParam = search ? `&letter=${search}` : "";
    await ApiGet(`privacy/find?page=${page}&limit=${countPerPage}${searchParam}`)
      .then((res: any) => {
        setLoadingData(false);
        setIsPrivacyData(res?.data?.data);
        setCount(res?.data?.count ?? 0);
      })
      .catch(() => {
        setLoadingData(false);
      });
  };

  const validateforUserData = () => {
    let isFormValid = true;
    const errors: any = {};

    if (!inputValue?.privacyName_ger?.trim()) {
      isFormValid = false;
      errors["privacyName_ger"] = "*Bitte geben Sie den Namen ein (DE)";
    }
    if (!inputValue?.privacyName?.trim()) {
      isFormValid = false;
      errors["privacyName"] = "*Bitte geben Sie den Namen ein (EN)";
    }
    if (!editorState?.content_ger) {
      isFormValid = false;
      errors["privacyDescription_ger"] = "*Bitte Beschreibung eingeben (DE)";
    }
    if (!editorState?.content) {
      isFormValid = false;
      errors["privacyDescription"] = "*Bitte Beschreibung eingeben (EN)";
    }

    setErrors(errors);
    return isFormValid;
  };

  const addPrivacydetails = (e: any) => {
    e.preventDefault();
    if (validateforUserData()) {
      setLoading(true);
      const data = {
        privacyName: inputValue?.privacyName,
        privacyName_ger: inputValue?.privacyName_ger,
        privacyDescription: editorState?.content,
        privacyDescription_ger: editorState?.content_ger,
      };

      ApiPost(`privacy/create`, data)
        .then((res: any) => {
          if (res?.status === 200) {
            setIsPrivacyModal(false);
            setLoading(false);
            getPrivacyDetails();
            toast.success("Datenschutz-Bestimmungen erfolgreich hinzugefügt");
            setInputValue({});
            setEditorState({ content: "", content_ger: "" });
          } else {
            setLoading(false);
            toast.error(res?.data?.message || "Etwas ist schief gelaufen...");
          }
        })
        .catch((err: any) => {
          setLoading(false);
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
        });
    }
  };

  const editPrivacyDetails = (e: any) => {
    e.preventDefault();
    if (validateforUserData()) {
      setLoading(true);
      const data = {
        privacyName: inputValue?.privacyName,
        privacyName_ger: inputValue?.privacyName_ger,
        privacyDescription: editorState?.content,
        privacyDescription_ger: editorState?.content_ger,
      };
      ApiPut(`privacy/update?id=${idForPrivacyEdit}`, data)
        .then(() => {
          setIsEditApi(false);
          setIsPrivacyModal(false);
          setLoading(false);
          toast.success("Datenschutz-Bestimmungen erfolgreich aktualisiert");
          setInputValue({});
          setEditorState({ content: "", content_ger: "" });
          getPrivacyDetails();
        })
        .catch((err: any) => {
          setLoading(false);
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
        });
    }
  };

  const deleteThisPrivacy = async () => {
    await ApiDelete(`privacy/delete?id=${privacyIdForDelete}`)
      .then(() => {
        setShow(false);
        toast.success("Datenschutzrichtlinie erfolgreich gelöscht");
        getPrivacyDetails();
      })
      .catch((error: any) => {
        toast.error(error?.response?.data?.message || "Etwas ist schief gelaufen...");
      });
  };

  // Content is sanitized via the createSanitizedMarkup utility (uses DOMPurify)
  const columns: TableColumnDef[] = [
    {
      header: "Name",
      render: (row: any) => (lang === "de" ? row?.privacyName_ger : row?.privacyName) || "-",
      getValue: (row: any) => lang === "de" ? row?.privacyName_ger : row?.privacyName,
    },
    {
      header: "Erstellt am",
      render: (row: any) => moment(row?.createdAt).format("DD.MM.YYYY"),
      getValue: (row: any) => row?.createdAt,
    },
    {
      header: "Aktion",
      render: (row: any) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {canEdit && (
            <Tooltip title="Privatsphäre bearbeiten" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  setIsPrivacyModal(true);
                  setIdForPrivacyEdit(row?._id);
                  setIsEditApi(true);
                  setInputValue({
                    privacyName: row?.privacyName,
                    privacyName_ger: row?.privacyName_ger,
                  });
                  setEditorState({
                    content: row?.privacyDescription || "",
                    content_ger: row?.privacyDescription_ger || "",
                  });
                }}
              >
                <CreateIcon />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Privatsphäre löschen" arrow>
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setPrivacyIdForDelete(row?._id);
                  setShow(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <ToastContainer />
      <Paper sx={{ p: 1 }}>
        <Box sx={{ p: 2, mb: 2 }}>
          <PageHeader
            title="Privatsphäre"
            onSearch={(val) => { setSearch(val); setPage(1); }}
            onAdd={canCreate ? () => setIsPrivacyModal(true) : undefined}
          />

          <LanguageTabs value={lang} onChange={setLang} />

          {loadingData ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataTable
              rows={isPrivacyData || []}
              columns={columns}
              page={page}
              count={count}
              rowsPerPage={countPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setCountPerPage}
            />
          )}
        </Box>
      </Paper>

      <Dialog open={show} onClose={handleClose}>
        <DialogTitle sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Löschen bestätigen
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie diesen Datenschutz-Bestimmungen löschen
            möchten?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">Nein</Button>
          <Button onClick={() => deleteThisPrivacy()} color="error" variant="contained">Ja</Button>
        </DialogActions>
      </Dialog>

      {isPrivacyModal && (
        <Dialog fullScreen open={isPrivacyModal} onClose={handlePrivacyModalClose}>
          <Toolbar>
            <IconButton edge="end" color="inherit" onClick={handlePrivacyModalClose} aria-label="close" sx={{ ml: "auto" }}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
          <List>
            <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
              {/* Column headers */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Box sx={{ width: { xs: "100%", lg: "16.66%" } }} />
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Deutsch</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>English</Typography>
                </Box>
              </Box>

              {/* Name row */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Name</Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small" fullWidth variant="outlined"
                    name="privacyName_ger"
                    value={inputValue?.privacyName_ger || ""}
                    onChange={handleOnChange}
                  />
                  <Typography variant="caption" color="error">{errors?.privacyName_ger}</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small" fullWidth variant="outlined"
                    name="privacyName"
                    value={inputValue?.privacyName || ""}
                    onChange={handleOnChange}
                  />
                  <Typography variant="caption" color="error">{errors?.privacyName}</Typography>
                </Box>
              </Box>

              {/* Description row */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Beschreibung</Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <SelfHostedEditor
                    onChange={(_event: any, editor: any) => {
                      setEditorState((prev) => ({ ...prev, content_ger: editor?.getContent() }));
                    }}
                    initialValue={editorState.content_ger}
                  />
                  <Typography variant="caption" color="error">{errors?.privacyDescription_ger}</Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <SelfHostedEditor
                    onChange={(_event: any, editor: any) => {
                      setEditorState((prev) => ({ ...prev, content: editor?.getContent() }));
                    }}
                    initialValue={editorState.content}
                  />
                  <Typography variant="caption" color="error">{errors?.privacyDescription}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  sx={{ mr: 2 }}
                  disabled={loading}
                  onClick={(e) => { isEditApi ? editPrivacyDetails(e) : addPrivacydetails(e); }}
                >
                  {isEditApi ? "Speichern" : "Hinzufügen"}
                  {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
                </Button>
              </Box>
            </Box>
          </List>
        </Dialog>
      )}
    </>
  );
};

export default Privacy;
